import { z } from "zod";
import { previewCommercialFiscalExtract, } from "./commercial-fiscal-extract-preview.js";
import { FISCAL_EXTRACT_BLOCKS, commercialFiscalExtractResultSchema, commercialFiscalExtractSchema, } from "./commercial-fiscal-extract.js";
const sha = z.string().regex(/^[a-f0-9]{64}$/);
const identity = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]{0,199}$/);
const revisionSchema = z
    .object({
    organizationId: z.string().uuid(),
    environment: z.literal("dev"),
    documentId: identity,
    revisionId: identity,
    producerVersion: identity,
    receipt: commercialFiscalExtractResultSchema,
})
    .strict();
/** Local declarations only: matching identifiers never authenticate a tenant or seller. */
export const commercialFiscalExtractDeltaInputSchema = z
    .object({
    schemaVersion: z.literal("commercial-fiscal-extract-delta-input.v1"),
    before: revisionSchema,
    after: revisionSchema,
})
    .strict();
const sourceRowSchema = z
    .object({
    recordSha256: sha,
    locators: z
        .array(z
        .object({
        sheet: z.string(),
        value: z.string(),
        textStart: z.number().int().nonnegative(),
        textEnd: z.number().int().nonnegative(),
    })
        .strict())
        .min(1)
        .max(2),
    diagnostics: z.array(z.string()),
})
    .strict();
const sideSchema = z
    .object({
    revisionId: identity,
    producerVersion: identity,
    profile: z.literal("COMMERCIAL_FISCAL_PARAMETERS_V1"),
    extractorVersion: z.literal("manual-fiscal-json-v1"),
    parentWorkbookSha256: sha,
    extractSha256: sha,
    selectionSha256: sha,
    receiptKind: z.enum(["LOCAL_PREVIEW", "DECLARED_COMMIT_RECEIPT"]),
})
    .strict();
export const commercialFiscalExtractDeltaSchema = z
    .object({
    schemaVersion: z.literal("commercial-fiscal-extract-delta.v1"),
    state: z.literal("BLOCKED_REVIEW_PREVIEW"),
    organizationId: z.string().uuid(),
    environment: z.literal("dev"),
    documentId: identity,
    identityVerification: z.literal("CALLER_DECLARED_UNVERIFIED"),
    parentVerification: z.literal("DECLARED_UNVERIFIED"),
    layoutVerification: z.literal("DECLARED_PROFILE_MAPPING"),
    direction: z.literal("CALLER_SELECTED_BEFORE_AFTER"),
    before: sideSchema,
    after: sideSchema,
    comparisonSha256: sha,
    comparison: z.enum(["SAME_SELECTED_SCOPE", "SELECTION_CHANGED", "PRODUCER_VERSION_CHANGED"]),
    rows: z
        .array(z
        .object({
        block: z.enum(Object.keys(FISCAL_EXTRACT_BLOCKS)),
        row: z.number().int().positive(),
        status: z.enum(["UNCHANGED", "CHANGED", "NOT_COMPARABLE"]),
        before: sourceRowSchema.nullable(),
        after: sourceRowSchema.nullable(),
        changedColumns: z.array(z.string().regex(/^[A-M]$/)).max(13),
    })
        .strict())
        .max(1000),
    counts: z
        .object({
        unchanged: z.number().int().nonnegative(),
        changed: z.number().int().nonnegative(),
        notComparable: z.number().int().nonnegative(),
        approvedRules: z.literal(0),
        publishedRules: z.literal(0),
    })
        .strict(),
    blockers: z.array(z.enum([
        "SOURCE_PROVENANCE_UNVERIFIED",
        "OFFICIAL_EVIDENCE_REQUIRED",
        "LEGAL_VALIDITY_UNKNOWN",
        "PRODUCT_APPLICABILITY_UNRESOLVED",
        "SELECTION_CHANGED",
        "PRODUCER_VERSION_CHANGED",
    ])),
    legalValidity: z.literal("UNKNOWN"),
    executable: z.literal(false),
    autoPublished: z.literal(false),
    sourceDeletionInferred: z.literal(false),
})
    .strict();
export class CommercialFiscalExtractDeltaError extends Error {
    code;
    constructor(code) {
        super(code);
        this.code = code;
    }
}
const fail = (code) => {
    throw new CommercialFiscalExtractDeltaError(code);
};
const compare = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
const canonical = (value) => JSON.stringify(value, (_key, item) => item && typeof item === "object" && !Array.isArray(item)
    ? Object.fromEntries(Object.entries(item).sort(([a], [b]) => compare(a, b)))
    : item);
async function checkedRevision(side, digest) {
    const receipt = side.receipt;
    let extract;
    try {
        extract = commercialFiscalExtractSchema.parse(JSON.parse(receipt.canonicalSnapshot));
    }
    catch {
        return fail("RECEIPT_INTEGRITY_FAILED");
    }
    const prepared = await previewCommercialFiscalExtract({ schemaVersion: "1.0.0", mode: "DRY_RUN", extract }, side.environment, digest);
    // Retention metadata is a caller claim, never a source of higher trust.
    const normalized = commercialFiscalExtractResultSchema.parse({
        ...receipt,
        mode: "DRY_RUN",
        extractId: null,
        createdAt: null,
        createdBy: null,
        reused: false,
    });
    if (canonical(normalized) !== canonical(prepared))
        fail("RECEIPT_INTEGRITY_FAILED");
    if (!prepared.persistable)
        fail("EXTRACT_NOT_PERSISTABLE");
    if (receipt.mode === "COMMIT" && (!receipt.extractId || !receipt.createdAt || !receipt.createdBy))
        fail("RECEIPT_INTEGRITY_FAILED");
    if (receipt.mode === "DRY_RUN" &&
        (receipt.extractId !== null ||
            receipt.createdAt !== null ||
            receipt.createdBy !== null ||
            receipt.reused))
        fail("RECEIPT_INTEGRITY_FAILED");
    const selection = {
        sheets: extract.sheets
            .map((s) => ({
            ...s,
            headers: s.headers
                .map((h) => ({
                ...h,
                mapping: [...h.mapping].sort((a, b) => compare(a.column, b.column)),
            }))
                .sort((a, b) => compare(a.block, b.block)),
        }))
            .sort((a, b) => compare(a.name, b.name)),
        rows: extract.rows
            .map((r) => ({
            sheet: r.sheet,
            block: r.block,
            row: r.row,
            columns: r.cells.map((c) => c.column).sort(compare),
        }))
            .sort((a, b) => compare(a.sheet, b.sheet) || compare(a.block, b.block) || a.row - b.row),
    };
    const summary = {
        revisionId: side.revisionId,
        producerVersion: side.producerVersion,
        profile: extract.profile,
        extractorVersion: extract.extractorVersion,
        parentWorkbookSha256: receipt.parentWorkbookSha256,
        extractSha256: receipt.extractSha256,
        selectionSha256: await digest(canonical(selection)),
        receiptKind: receipt.mode === "COMMIT" ? "DECLARED_COMMIT_RECEIPT" : "LOCAL_PREVIEW",
    };
    const rows = new Map();
    for (let i = 0; i < prepared.rows.length; i++) {
        const row = prepared.rows[i];
        if (!row)
            continue;
        const original = extract.rows.find((r) => r.sheet === row.sheet && r.block === row.block && r.row === row.row);
        if (!original)
            return fail("RECEIPT_INTEGRITY_FAILED");
        const key = `${row.block}:${row.row}`;
        const locator = {
            sheet: row.sheet,
            value: row.locator.value,
            textStart: row.locator.textStart,
            textEnd: row.locator.textEnd,
        };
        const existing = rows.get(key);
        if (existing)
            existing.source.locators.push(locator);
        else
            rows.set(key, {
                source: {
                    recordSha256: row.recordSha256,
                    locators: [locator],
                    diagnostics: [...row.diagnostics].sort(compare),
                },
                cells: original.cells,
            });
    }
    return { summary, rows };
}
/** Compare supplied JSON observations, never evaluate formulas, infer absence, or qualify a rule. */
export async function compareCommercialFiscalExtracts(input, digest) {
    const parsed = commercialFiscalExtractDeltaInputSchema.safeParse(input);
    if (!parsed.success)
        return fail("INVALID_INPUT");
    const { before, after } = parsed.data;
    if (before.organizationId !== after.organizationId ||
        before.environment !== after.environment ||
        before.documentId !== after.documentId)
        fail("IDENTITY_MISMATCH");
    let left;
    let right;
    try {
        left = await checkedRevision(before, digest);
        right = await checkedRevision(after, digest);
    }
    catch (error) {
        if (error instanceof CommercialFiscalExtractDeltaError)
            throw error;
        return fail("RECEIPT_INTEGRITY_FAILED");
    }
    if (before.revisionId === after.revisionId &&
        (left.summary.parentWorkbookSha256 !== right.summary.parentWorkbookSha256 ||
            left.summary.extractSha256 !== right.summary.extractSha256 ||
            before.producerVersion !== after.producerVersion))
        fail("REVISION_CONTENT_CONFLICT");
    const comparison = before.producerVersion !== after.producerVersion
        ? "PRODUCER_VERSION_CHANGED"
        : left.summary.selectionSha256 !== right.summary.selectionSha256
            ? "SELECTION_CHANGED"
            : "SAME_SELECTED_SCOPE";
    const rows = [];
    const keys = [...new Set([...left.rows.keys(), ...right.rows.keys()])].sort(compare);
    for (const key of keys) {
        const a = left.rows.get(key);
        const b = right.rows.get(key);
        const split = key.lastIndexOf(":");
        const canCompare = comparison === "SAME_SELECTED_SCOPE" && a && b;
        const changed = !!canCompare && a.source.recordSha256 !== b.source.recordSha256;
        rows.push({
            block: key.slice(0, split),
            row: Number(key.slice(split + 1)),
            status: !canCompare ? "NOT_COMPARABLE" : changed ? "CHANGED" : "UNCHANGED",
            before: a?.source ?? null,
            after: b?.source ?? null,
            changedColumns: changed
                ? a.cells
                    .filter((c) => canonical(c.observation) !==
                    canonical(b.cells.find((v) => v.column === c.column)?.observation))
                    .map((c) => c.column)
                    .sort(compare)
                : [],
        });
    }
    const report = {
        schemaVersion: "commercial-fiscal-extract-delta.v1",
        state: "BLOCKED_REVIEW_PREVIEW",
        organizationId: before.organizationId,
        environment: before.environment,
        documentId: before.documentId,
        identityVerification: "CALLER_DECLARED_UNVERIFIED",
        parentVerification: "DECLARED_UNVERIFIED",
        layoutVerification: "DECLARED_PROFILE_MAPPING",
        direction: "CALLER_SELECTED_BEFORE_AFTER",
        before: left.summary,
        after: right.summary,
        comparison,
        rows,
        counts: {
            unchanged: rows.filter((r) => r.status === "UNCHANGED").length,
            changed: rows.filter((r) => r.status === "CHANGED").length,
            notComparable: rows.filter((r) => r.status === "NOT_COMPARABLE").length,
            approvedRules: 0,
            publishedRules: 0,
        },
        blockers: [
            "SOURCE_PROVENANCE_UNVERIFIED",
            "OFFICIAL_EVIDENCE_REQUIRED",
            "LEGAL_VALIDITY_UNKNOWN",
            "PRODUCT_APPLICABILITY_UNRESOLVED",
            ...(comparison === "SAME_SELECTED_SCOPE" ? [] : [comparison]),
        ],
        legalValidity: "UNKNOWN",
        executable: false,
        autoPublished: false,
        sourceDeletionInferred: false,
    };
    return commercialFiscalExtractDeltaSchema.parse({
        ...report,
        comparisonSha256: await digest(canonical(report)),
    });
}
//# sourceMappingURL=commercial-fiscal-extract-delta.js.map